import React, { useMemo, useState } from 'react';
import { getProjectPermissions } from '@/services/projects';
import CategoryPermissionsMatrixEditor from '@/components/permissions/CategoryPermissionsMatrixEditor';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import PermissionsEditor from '@/components/permissions/PermissionsEditor';
import { ProjectPermissionType, ProjectMemberPermissions, ProjectCategoryPermission } from '@/types';
import { useAsyncState } from '@/utils/hooks';
import { Trans } from '@lingui/react/macro';
import ProjectFeeSetPermissionsEditor from './ProjectFeeSetPermissionsEditor';
import { AlertBanner, CheckboxGroup, Radio, RadioGroup, Tabs } from '@ygtechnologies/rubus-ui';
import { getProjectFeeSets } from '@/services/fees';

interface ProjectPermissionsEditorProps {
    projectId: number;
    value: ProjectMemberPermissions;
    onChange(newValue: ProjectMemberPermissions): void;
}

enum TabId {
    UserType = 'UserType',
    SystemPermissions = 'SystemPermissions',
    CategoryPermissions = 'CategoryPermissions',
    FeePermissions = 'FeePermissions',
    Analytics = 'Analytics',
}

const analyticsPermissionTypes = ['IsChangeManagementAnalyticsAdmin'];

const ProjectPermissionsEditor: React.FC<ProjectPermissionsEditorProps> = ({ value, onChange, projectId }) => {
    const [permissions] = useAsyncState(() => getProjectPermissions(), [], null);
    const [projectFeeSets] = useAsyncState(() => getProjectFeeSets(projectId), [projectId], null);
    const [currentTab, setCurrentTab] = useState(TabId.UserType);

    const systemPermissions = useMemo(() => {
        if (!permissions) {
            return null;
        }

        return permissions.filter((x) => !analyticsPermissionTypes.includes(x.value));
    }, [permissions]);

    const analyticsPermissions = useMemo(() => {
        if (!permissions) {
            return null;
        }

        return permissions.filter((x) => analyticsPermissionTypes.includes(x.value));
    }, [permissions]);

    const doChange = (obj: Partial<ProjectMemberPermissions>) => {
        const newValue = {
            ...value,
            ...obj,
        };

        triggerHandler(onChange, newValue);
    };

    const handleProjectPermissionChange = (projectPermissions: string[]) => {
        doChange({ projectPermissions });
    };

    const handleCategoryPermissionsChange = (categoryPermissions: ProjectCategoryPermission[]) => {
        doChange({ categoryPermissions });
    };

    const handleProjectFeeSetChange = (allowedProjectFeeSetIds: number[]) => {
        doChange({ allowedProjectFeeSetIds });
    };

    if (!systemPermissions || !analyticsPermissions || !projectFeeSets) {
        return null;
    }

    return (
        <div>
            <div>
                <Tabs activeId={currentTab} onChange={(id) => setCurrentTab(id as TabId)} variant="underline">
                    <Tabs.Tab id={TabId.UserType} label={<Trans>User type</Trans>} />
                    {!value.isAdministrator && (
                        <Tabs.Tab id={TabId.SystemPermissions} label={<Trans>General permissions</Trans>} />
                    )}
                    {!value.isAdministrator && (
                        <Tabs.Tab id={TabId.FeePermissions} label={<Trans>Allowed fees</Trans>} />
                    )}
                    {!value.isAdministrator && (
                        <Tabs.Tab id={TabId.CategoryPermissions} label={<Trans>Profession permissions</Trans>} />
                    )}
                    <Tabs.Tab id={TabId.Analytics} label={<Trans>Analytics</Trans>} />
                </Tabs>
                {currentTab === TabId.UserType && (
                    <>
                        <h4>
                            <Trans>User type</Trans>
                        </h4>
                        <RadioGroup>
                            <Radio
                                onChange={(e) => doChange({ isAdministrator: true })}
                                checked={value.isAdministrator}
                                inputSize="md">
                                <Trans>Admin</Trans>
                            </Radio>
                            <Radio
                                onChange={(e) => doChange({ isAdministrator: false })}
                                checked={!value.isAdministrator}
                                inputSize="md">
                                <Trans>Member</Trans>
                            </Radio>
                        </RadioGroup>
                        {value.isAdministrator && (
                            <AlertBanner type="warning">
                                <Trans>Making a user an admin will give them all permissions on this project.</Trans>
                            </AlertBanner>
                        )}
                    </>
                )}
                {currentTab === TabId.SystemPermissions && !value.isAdministrator && (
                    <>
                        <h4>
                            <Trans>General permissions</Trans>
                        </h4>
                        <PermissionsEditor<ProjectPermissionType>
                            permissions={systemPermissions}
                            value={value.projectPermissions || []}
                            isAdministrator={false}
                            onChange={handleProjectPermissionChange}
                        />
                    </>
                )}
                {currentTab === TabId.FeePermissions && !value.isAdministrator && (
                    <>
                        <h4>
                            <Trans>Allowed fees</Trans>
                        </h4>
                        <ProjectFeeSetPermissionsEditor
                            projectFeeSets={projectFeeSets}
                            value={value.allowedProjectFeeSetIds}
                            onChange={handleProjectFeeSetChange}
                        />
                    </>
                )}
                {currentTab === TabId.CategoryPermissions && !value.isAdministrator && (
                    <>
                        <h4>
                            <Trans>Profession permissions</Trans>
                        </h4>
                        <br />
                        <CategoryPermissionsMatrixEditor
                            value={value.categoryPermissions || []}
                            onChange={handleCategoryPermissionsChange}
                        />
                    </>
                )}
                {currentTab === TabId.Analytics && (
                    <>
                        <h4>
                            <Trans>Analytics permissions</Trans>
                        </h4>
                        <PermissionsEditor<ProjectPermissionType>
                            permissions={analyticsPermissions}
                            value={value.projectPermissions || []}
                            isAdministrator={false}
                            onChange={handleProjectPermissionChange}
                        />
                    </>
                )}
            </div>
            {/* {!value.isAdministrator && (
                <>
                    <div className={theme.row}>
                        <div className={theme.half}>
                            <h4>
                                <Trans>General permissions</Trans>
                            </h4>
                            <PermissionsEditor<ProjectPermissionType>
                                permissions={permissions}
                                value={value.projectPermissions || []}
                                isAdministrator={false}
                                onChange={handleProjectPermissionChange}
                            />
                        </div>

                        <div className={theme.half}>
                            <h4>
                                <Trans>Allowed fees</Trans>
                            </h4>
                            <ProjectFeeSetPermissionsEditor
                                projectFeeSets={projectFeeSets}
                                value={value.allowedProjectFeeSetIds}
                                onChange={handleProjectFeeSetChange}
                            />
                        </div>
                    </div>

                    <hr />

                    <div className={theme.row}>
                        <div className={theme.full}>
                            <h4>
                                <Trans>Profession permissions</Trans>
                            </h4>
                            <CategoryPermissionsMatrixEditor
                                value={value.categoryPermissions || []}
                                onChange={handleCategoryPermissionsChange}
                            />
                        </div>
                    </div>
                </>
            )} */}
        </div>
    );
};

export default ProjectPermissionsEditor;
